import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, WritableSignal, inject, signal } from '@angular/core';
import { BehaviorSubject, Observable, catchError, filter, finalize, map, of, take, throwError } from 'rxjs';
import { ErrorService } from '../error/error.service';
import { LoadingObject, LoadingService } from '../loaders';
import {
  GetInterculturalistProfileSuccessResponse,
  InterculturalistRoleAssignedOnRegistrationResponse,
  InterculturalistProfileError,
  InterculturalistProfileResource,
  InterculturalistProfileViewModel,
  SaveInterculturalistProfileRequestBody,
  SaveInterculturalistProfileSuccessResponse,
  ToggleInterculturalistReviewSuccessResponse,
} from './interculturalist-profile.types';

@Injectable({
  providedIn: 'root',
})
export class InterculturalistProfileService {
  private readonly http = inject(HttpClient);
  private readonly loadingService = inject(LoadingService);
  private readonly errorService = inject(ErrorService);

  private readonly _profile = new BehaviorSubject<InterculturalistProfileViewModel | null | undefined>(undefined);
  private readonly _interculturalistRoleAssignedOnRegistration = new BehaviorSubject<boolean | undefined>(undefined);
  private readonly _loadingObject = signal<LoadingObject>({});
  private readonly _error = signal<string | null>(null);

  clearData(): void {
    this._profile.next(undefined);
    this._interculturalistRoleAssignedOnRegistration.next(undefined);
    this._error.set(null);
    this._loadingObject.set({});
  }

  get loadingObject(): { [key: string]: boolean } {
    return this._loadingObject();
  }

  get loadingObjectSignal(): WritableSignal<LoadingObject> {
    return this._loadingObject;
  }

  get error(): string | null {
    return this._error();
  }

  setLoadingStatus(key: string, status: boolean): void {
    this._loadingObject.set(this.loadingService.setLoadingStatus(key, status, this._loadingObject()));
  }

  get interculturalistProfile$(): Observable<InterculturalistProfileViewModel | null | undefined> {
    return this._profile.asObservable();
  }

  get interculturalistProfileState(): InterculturalistProfileViewModel | null | undefined {
    return this._profile.getValue();
  }

  get interculturalistRoleAssignedOnRegistration$(): Observable<boolean | undefined> {
    return this._interculturalistRoleAssignedOnRegistration.asObservable();
  }

  get interculturalistRoleAssignedOnRegistrationState(): boolean | undefined {
    return this._interculturalistRoleAssignedOnRegistration.getValue();
  }

  getInterculturalistProfile(freshFromServer = false): Observable<InterculturalistProfileViewModel | null> {
    if (this.loadingObjectSignal()['interculturalistProfile']) {
      return this.interculturalistProfile$.pipe(
        filter((profile) => profile !== undefined),
        take(1),
        map((profile) => profile ?? null),
      );
    }

    if (!freshFromServer && this.interculturalistProfileState !== undefined) {
      return of(this.interculturalistProfileState ?? null);
    }

    this.setLoadingStatus('interculturalistProfile', true);
    this._error.set(null);

    return this.http.get<GetInterculturalistProfileSuccessResponse>('api/v1/interculturalist-profile').pipe(
      map((response) => {
        const profile = response?.data ? this.mapResourceToViewModel(response.data) : null;
        this._profile.next(profile);
        return profile;
      }),
      catchError((errorResponse: HttpErrorResponse) => {
        if (errorResponse.status === 404) {
          this._profile.next(null);
          return of(null);
        }

        const normalisedError = this.normaliseError(errorResponse, 'error.could_not_load');
        this._error.set(normalisedError.message);
        return throwError(() => normalisedError);
      }),
      finalize(() => {
        this.setLoadingStatus('interculturalistProfile', false);
      }),
    );
  }

  saveInterculturalistProfile(payload: SaveInterculturalistProfileRequestBody): Observable<InterculturalistProfileViewModel> {
    this.setLoadingStatus('interculturalistProfile', true);
    this._error.set(null);

    return this.http.post<SaveInterculturalistProfileSuccessResponse>('api/v1/interculturalist-profile', this.normalisePayload(payload)).pipe(
      map((response) => {
        const profile = this.mapResourceToViewModel(response.data);
        this._profile.next(profile);
        return profile;
      }),
      catchError((errorResponse: HttpErrorResponse) => {
        const normalisedError = this.normaliseError(errorResponse, 'error.not_done_try_again');
        this._error.set(normalisedError.message);
        return throwError(() => normalisedError);
      }),
      finalize(() => {
        this.setLoadingStatus('interculturalistProfile', false);
      }),
    );
  }

  toggleReviewRequest(): Observable<InterculturalistProfileViewModel> {
    this.setLoadingStatus('toggleReviewRequest', true);
    this._error.set(null);

    return this.http.patch<ToggleInterculturalistReviewSuccessResponse>('api/v1/user-interculturalist-profile/toggle-review', {}).pipe(
      map((response) => {
        const profile = this.mapResourceToViewModel(response.data);
        this._profile.next(profile);
        return profile;
      }),
      catchError((errorResponse: HttpErrorResponse) => {
        const normalisedError = this.normaliseError(errorResponse, 'error.not_done_try_again');
        this._error.set(normalisedError.message);
        return throwError(() => normalisedError);
      }),
      finalize(() => {
        this.setLoadingStatus('toggleReviewRequest', false);
      }),
    );
  }

  getInterculturalistRoleAssignedOnRegistration(freshFromServer = false): Observable<boolean> {
    if (this.loadingObjectSignal()['interculturalistRoleAssignedOnRegistration']) {
      return this.interculturalistRoleAssignedOnRegistration$.pipe(
        filter((assigned) => assigned !== undefined),
        take(1),
        map((assigned) => assigned === true),
      );
    }

    if (!freshFromServer && this.interculturalistRoleAssignedOnRegistrationState !== undefined) {
      return of(this.interculturalistRoleAssignedOnRegistrationState === true);
    }

    this.setLoadingStatus('interculturalistRoleAssignedOnRegistration', true);
    this._error.set(null);

    return this.http.get<InterculturalistRoleAssignedOnRegistrationResponse>('api/v1/registration-pending/interculturalist-role-assigned').pipe(
      map((response) => {
        const assigned = response?.data === true;
        this._interculturalistRoleAssignedOnRegistration.next(assigned);
        return assigned;
      }),
      catchError((errorResponse: HttpErrorResponse) => {
        const normalisedError = this.normaliseError(errorResponse, 'error.could_not_load');
        this._error.set(normalisedError.message);
        return throwError(() => normalisedError);
      }),
      finalize(() => {
        this.setLoadingStatus('interculturalistRoleAssignedOnRegistration', false);
      }),
    );
  }

  private normalisePayload(payload: SaveInterculturalistProfileRequestBody): SaveInterculturalistProfileRequestBody {
    return {
      self_assessment: payload.self_assessment ?? null,
      statement: this.normaliseNullableString(payload.statement),
      work_status: payload.work_status ?? null,
    };
  }

  private normaliseNullableString(value: string | null | undefined): string | null {
    if (typeof value !== 'string') {
      return null;
    }

    const trimmedValue = value.trim();
    return trimmedValue.length ? trimmedValue : null;
  }

  private mapResourceToViewModel(resource: InterculturalistProfileResource): InterculturalistProfileViewModel {
    return {
      id: resource.id,
      userId: resource.user_id,
      selfAssessment: this.coerceToNullableBoolean(resource.self_assessment),
      statement: resource.statement,
      workStatus: resource.work_status,
      reviewerFeedback: resource.reviewer_feedback ?? null,
      reviewRequestedAt: resource.review_requested_at ? new Date(resource.review_requested_at) : null,
      reviewStartedAt: resource.review_started_at ? new Date(resource.review_started_at) : null,
      reviewCompletedAt: resource.review_completed_at ? new Date(resource.review_completed_at) : null,
      updatedAt: resource.updated_at ? new Date(resource.updated_at) : null,
    };
  }

  private coerceToNullableBoolean(value: unknown): boolean | null {
    if (value === null || value === undefined) {
      return null;
    }

    if (value === true || value === 1 || value === '1') {
      return true;
    }

    if (value === false || value === 0 || value === '0') {
      return false;
    }

    return Boolean(value);
  }

  private normaliseError(errorResponse: HttpErrorResponse, fallbackMessage: string): InterculturalistProfileError {
    const normalisedError = this.errorService.normaliseHttpError(errorResponse, fallbackMessage);

    if (normalisedError.status === 401) {
      return {
        ...normalisedError,
        message: 'authentication.login_required_feature',
        status: 401,
      };
    }

    return {
      message: normalisedError.message,
      status: normalisedError.status ?? 0,
      errors: normalisedError.errors,
    };
  }
}